import React, { useMemo, useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";

/* Multi-color Google Maps Icon */
const GoogleMapsIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24 4C15.163 4 8 11.163 8 20c0 10.5 13.5 22.5 15.2 23.9.47.39 1.13.39 1.6 0C26.5 42.5 40 30.5 40 20c0-8.837-7.163-16-16-16z"
      fill="#4285F4"
    />
    <path
      d="M24 4C15.163 4 8 11.163 8 20c0 4.1 1.5 7.8 4 10.6l12-10.6V4z"
      fill="#EA4335"
    />
    <path
      d="M24 20l-12 10.6c2.7 3.1 6.5 6.8 12 13.3V20z"
      fill="#FBBC05"
    />
    <path
      d="M24 20v23.9c5.5-6.5 9.3-10.2 12-13.3L24 20z"
      fill="#34A853"
    />
    <circle cx="24" cy="20" r="7" fill="#FFFFFF" />
  </svg>
);

const GoogleRoutePreview = ({
  destinationLat,
  destinationLng,
  destinationAddress,
  destinationName = "Destination",
}) => {
  const [liveCoords, setLiveCoords] = useState(null);

  // Poll high-precision satellite GPS just like the geofence does
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Only accept true satellite GPS (accuracy <= 200m)
        if (pos.coords.accuracy <= 200) {
          setLiveCoords({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        }
      },
      (err) => console.warn("Route preview GPS fallback:", err.message),
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  }, []);

  // Destination query parameter
  const destQuery = useMemo(() => {
    if (destinationLat && destinationLng) {
      return `${destinationLat},${destinationLng}`;
    }
    return encodeURIComponent(destinationAddress || destinationName || "Vijayawada");
  }, [destinationLat, destinationLng, destinationAddress, destinationName]);

  // Use real hardware coordinates for the iframe route; fallback to Current+Location if not locked yet
  const originQuery = useMemo(() => {
    if (liveCoords?.lat && liveCoords?.lng) {
      return `${liveCoords.lat},${liveCoords.lng}`;
    }
    return "Current+Location";
  }, [liveCoords]);

  // In-app embedded iframe route URL
  const directionsEmbedUrl = useMemo(() => {
    return `https://maps.google.com/maps?saddr=${originQuery}&daddr=${destQuery}&output=embed`;
  }, [originQuery, destQuery]);

  // Native Google Maps deep link (always uses real hardware GPS without hardcoded origin)
  const nativeAppUrl = useMemo(() => {
    return `https://www.google.com/maps/dir/?api=1&destination=${destQuery}&travelmode=driving`;
  }, [destQuery]);

  const handleOpenGoogleMaps = () => {
    window.open(nativeAppUrl, "_blank");
  };

  return (
    <div
      onClick={handleOpenGoogleMaps}
      className="relative w-full h-64 rounded-2xl overflow-hidden border border-gray-200 shadow-xs bg-[#E8EEF5] cursor-pointer group select-none"
    >
      <iframe
        key={directionsEmbedUrl}
        title="Route Preview"
        src={directionsEmbedUrl}
        className="w-full h-full border-0 pointer-events-none group-hover:opacity-95 transition"
        loading="lazy"
        allowFullScreen={false}
      />

      {/* Accuracy Indicator */}
      <div className="absolute top-3 left-3 z-10 bg-slate-900/90 backdrop-blur-xs text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-md">
        <span
          className={`w-2 h-2 rounded-full ${
            liveCoords ? "bg-emerald-400 animate-ping" : "bg-amber-400"
          }`}
        />
        <span className="text-[10px] font-bold font-mono">
          {liveCoords ? "GPS Route Locked" : "Tap Map to Navigate"}
        </span>
      </div>

      {/* Open in Google Maps Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenGoogleMaps();
        }}
        className="absolute bottom-3 right-3 z-10 bg-slate-900 hover:bg-black text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg active:scale-95 transition cursor-pointer"
        style={{ fontFamily: "DMSans-Medium, sans-serif" }}
      >
        <GoogleMapsIcon className="w-4 h-4" />
        <span>Open in Google Maps</span>
        <ArrowUpRight className="w-3.5 h-3.5 text-gray-400" />
      </button>
    </div>
  );
};

export default GoogleRoutePreview;